import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, Copy, Check } from 'lucide-react'
import { SiteHeader } from '@/components/site-header'
import { Button } from '@/components/ui/button'

const MCP_URL = 'https://mcp.syllepsis.io/mcp'
const CLAUDE_CONNECTORS_URL =
  'https://claude.ai/customize/connectors?modal=add-custom-connector'

export default function AuthSuccessPage() {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    document.title = "You're connected — Syllepsis"

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    metaDescription.setAttribute(
      'content',
      'You are connected to the Syllepsis MCP service. Add it to your AI tool to get started.'
    )

    let metaRobots = document.querySelector('meta[name="robots"]')
    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.setAttribute('name', 'robots')
      document.head.appendChild(metaRobots)
    }
    metaRobots.setAttribute('content', 'noindex, nofollow')

    return () => {
      if (metaRobots && metaRobots.parentNode) {
        metaRobots.parentNode.removeChild(metaRobots)
      }
    }
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable — user can select the address manually.
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative py-16 md:py-24">
          <div className="container">
            <div className="mx-auto max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-lg border border-border bg-card p-8 shadow-lg md:p-12"
              >
                <div className="mb-6 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-4">
                    <CheckCircle
                      className="h-12 w-12 text-primary"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                <h1 className="mb-4 text-center text-3xl font-bold md:text-4xl">
                  You're connected to Syllepsis
                </h1>

                <p className="mb-8 text-center text-lg text-muted-foreground">
                  Your account is set up. The last step is to add the Syllepsis
                  connection to the AI tool you use, so it can draw on the
                  knowledge you've been given access to.
                </p>

                <div className="mb-8 rounded-lg border border-border p-5">
                  <h2 className="mb-1 text-lg font-semibold">
                    Your connection address
                  </h2>
                  <p className="mb-3 text-sm text-muted-foreground">
                    You'll paste this into your AI tool when you add the
                    connection.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 overflow-x-auto rounded-md border border-border bg-muted px-3 py-2 text-sm">
                      {MCP_URL}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopy}
                      aria-label="Copy address"
                    >
                      {copied ? (
                        <Check className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Copy className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="mb-6 rounded-lg border border-border p-5">
                  <h2 className="mb-1 text-lg font-semibold">Add it to Claude</h2>
                  <p className="mb-3 text-sm text-muted-foreground">
                    Claude has the most direct support for custom connectors, so
                    it's the quickest to set up.
                  </p>
                  <ol className="ml-5 list-decimal space-y-2 text-sm text-muted-foreground">
                    <li>
                      Open{' '}
                      <a
                        href={CLAUDE_CONNECTORS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        Claude's connector settings
                      </a>{' '}
                      and choose <strong>Add custom connector</strong>.
                    </li>
                    <li>
                      Paste the connection address above into the URL field and
                      confirm.
                    </li>
                  </ol>
                </div>

                <div className="mb-8 rounded-lg border border-border p-5">
                  <h2 className="mb-1 text-lg font-semibold">Other tools</h2>
                  <p className="text-sm text-muted-foreground">
                    Support for custom connectors (MCP) is expanding across AI
                    assistants. If yours supports it, add a custom connector
                    using the connection address above, through its own
                    settings.
                  </p>
                </div>

                <h2 className="mb-1 text-lg font-semibold">How it works</h2>
                <p className="mb-6 text-muted-foreground">
                  Once the connection is added, just use your AI tool as you
                  normally would. It will draw on the Syllepsis knowledge
                  automatically whenever it's relevant to what you're working on.
                </p>

                <p className="text-sm text-muted-foreground">
                  Need a hand getting connected? Email{' '}
                  <a
                    href="mailto:info@syllepsis.io"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    info@syllepsis.io
                  </a>{' '}
                  and we'll help.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
